import React, { useState, useEffect, useCallback, useRef } from 'react';
import stampytApi from '../services/stampyt';
import './StampytVO.css';

// ─── Configuration des vues guidées (pour le rendu des labels) ────────────────
const VUES = [
  { code: '3_4_avant',         label: '3/4 Avant gauche'   },
  { code: 'face_avant',        label: 'Face avant'         },
  { code: 'profil_gauche',     label: 'Profil gauche'      },
  { code: 'profil_droit',      label: 'Profil droit'       },
  { code: '3_4_arriere',       label: '3/4 Arrière droit'  },
  { code: 'face_arriere',      label: 'Face arrière'       },
  { code: 'habitacle_avant',   label: 'Habitacle avant'    },
  { code: 'habitacle_arriere', label: 'Habitacle arrière'  },
  { code: 'coffre',            label: 'Coffre'             },
  { code: 'compteur',          label: 'Compteur km'        },
];

const STATUT_LABELS = { en_cours: 'En cours', traite: 'Traité', publie: 'Publié' };
const STATUT_COLORS = { en_cours: '#f59e0b', traite: '#10b981', publie: '#3b82f6' };

// Convertit une taille en octets vers un format lisible (245 KB, 1.2 MB...)
function formatFileSize(bytes) {
  if (bytes == null || bytes === 0) return null;
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function StampytVO({ user, setHeaderAction }) {
  const [activeTab, setActiveTab] = useState('liste'); // 'liste' | 'detail' | 'fonds'

  // ── Vue Liste ────────────────────────────────────────────────────────────────
  const [vehicules, setVehicules] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [selectedVehicule, setSelectedVehicule] = useState(null);

  // ── Vue Détail ───────────────────────────────────────────────────────────────
  const [detailPhotos, setDetailPhotos] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [uploading, setUploading] = useState({});  // { [typePriseVue]: true/false }

  // ── Modale création véhicule ────────────────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ immatriculation: '', marque: '', modele: '' });
  const [creating, setCreating] = useState(false);

  // ── Fonds (arrière-plans) ──────────────────────────────────────────────────
  const [fonds, setFonds] = useState([]);
  const [fondsLoading, setFondsLoading] = useState(false);
  const [showFondModal, setShowFondModal] = useState(null); // photoId pour lequel on choisit un fond
  const [applyingFond, setApplyingFond] = useState(false);
  const applyingRef = useRef(false);
  const [showAddFondModal, setShowAddFondModal] = useState(false);
  const [newFondForm, setNewFondForm] = useState({ nom: '', file: null, concession_id: '' });
  const [uploadingFond, setUploadingFond] = useState(false);

  const handleCreateVehicule = async () => {
    if (!createForm.immatriculation.trim()) return alert('Immatriculation requise');
    setCreating(true);
    try {
      const res = await stampytApi.createVehicule(createForm);
      setShowCreateModal(false);
      setCreateForm({ immatriculation: '', marque: '', modele: '' });
      loadVehicules();
      // Ouvrir directement le détail pour ajouter les photos
      const vehicule = { id: res.data.vehicule_id, immatriculation: createForm.immatriculation, statut: 'en_cours' };
      handleOpenDetail(vehicule);
    } catch (err) {
      alert('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  // ─── Détourage ──────────────────────────────────────────────────────────────
  const handleProcessPhotos = async () => {
    if (!selectedVehicule) return;
    try {
      await stampytApi.processPhotos(selectedVehicule.id);
      loadDetailPhotos(selectedVehicule.id);
    } catch (err) {
      alert('Erreur lors du lancement du détourage');
    }
  };

  const handleProcessOnePhoto = async (photoId) => {
    if (!selectedVehicule) return;
    try {
      await stampytApi.processOnePhoto(selectedVehicule.id, photoId);
      loadDetailPhotos(selectedVehicule.id);
    } catch (err) {
      alert('Erreur lors du lancement du détourage');
    }
  };

  useEffect(() => {
    if (!setHeaderAction) return;
    if (activeTab === 'detail' && selectedVehicule) {
      setHeaderAction({
        primary: { label: '🖼️ Lancer le détourage', onClick: handleProcessPhotos },
      });
    } else if (activeTab === 'fonds') {
      setHeaderAction({
        primary: { label: '+ Nouveau fond', onClick: () => setShowAddFondModal(true) },
      });
    } else {
      setHeaderAction({
        primary: { label: '+ Nouveau véhicule', onClick: () => setShowCreateModal(true) },
      });
    }
  }, [activeTab, selectedVehicule, setHeaderAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Chargement de la liste ──────────────────────────────────────────────────
  const loadVehicules = useCallback(async () => {
    try {
      setListLoading(true);
      const res = await stampytApi.listVehicules();
      setVehicules(res.data.vehicules);
    } catch (err) {
      setListError('Erreur lors du chargement');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVehicules();
  }, [loadVehicules]);

  // ─── Nettoyage polling ───────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [pollingInterval]);

  // ─── Ouvrir le détail d'un véhicule ──────────────────────────────────────────
  const handleOpenDetail = (vehicule) => {
    setSelectedVehicule(vehicule);
    setActiveTab('detail');
    loadDetailPhotos(vehicule.id);
  };

  const loadDetailPhotos = async (vehiculeId) => {
    try {
      setDetailLoading(true);
      const res = await stampytApi.getUrls(vehiculeId);
      setDetailPhotos(res.data.photos);

      const hasProcessing = res.data.photos.some(
        (p) => p.statut_traitement === 'en_cours' || p.statut_traitement === 'en_attente'
      );

      if (hasProcessing) {
        const interval = setInterval(async () => {
          const updated = await stampytApi.getUrls(vehiculeId);
          setDetailPhotos(updated.data.photos);
          const stillProcessing = updated.data.photos.some(
            (p) => p.statut_traitement === 'en_cours' || p.statut_traitement === 'en_attente'
          );
          if (!stillProcessing) {
            clearInterval(interval);
            setPollingInterval(null);
            loadVehicules();
          }
        }, 3000);
        setPollingInterval(interval);
      }
    } catch (err) {
      console.error('Erreur chargement photos:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // ─── Upload de photo ──────────────────────────────────────────────────────────
  const handleUploadPhoto = async (vehiculeId, file, typePriseVue) => {
    const ordre = detailPhotos.filter(p => p.type_prise_vue === typePriseVue).length + 1;
    setUploading(prev => ({ ...prev, [typePriseVue]: true }));
    try {
      await stampytApi.uploadPhoto(vehiculeId, file, typePriseVue, ordre);
      loadDetailPhotos(vehiculeId);
    } catch (err) {
      console.error('Erreur upload:', err);
      alert('Erreur lors de l\'upload de la photo');
    } finally {
      setUploading(prev => ({ ...prev, [typePriseVue]: false }));
    }
  };

  const handleMultiUpload = async (vehiculeId, files) => {
    // Upload multiple photos sans type prédéfini — assigner les types dans l'ordre des VUES disponibles
    const existingTypes = new Set(detailPhotos.map(p => p.type_prise_vue));
    const availableTypes = VUES.map(v => v.code).filter(code => !existingTypes.has(code));

    for (let i = 0; i < files.length; i++) {
      const type = availableTypes[i] || `autre_${Date.now()}_${i}`;
      await handleUploadPhoto(vehiculeId, files[i], type);
    }
  };

  // ─── Fonds ────────────────────────────────────────────────────────────────────
  const loadFonds = useCallback(async () => {
    try {
      setFondsLoading(true);
      const res = await stampytApi.listFonds();
      setFonds(res.data.fonds);
    } catch (err) {
      console.error('Erreur chargement fonds:', err);
    } finally {
      setFondsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFonds();
  }, [loadFonds]);

  const handleApplyFond = async (photoId, fondId) => {
    if (applyingRef.current) return;
    applyingRef.current = true;
    setApplyingFond(true);
    try {
      await stampytApi.applyBackground(photoId, fondId);
      setShowFondModal(null);
      // Recharger les photos (nouvelles URLs signées S3 = pas de cache)
      if (selectedVehicule) {
        await loadDetailPhotos(selectedVehicule.id);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'application du fond');
    } finally {
      applyingRef.current = false;
      setApplyingFond(false);
    }
  };

  const handleAddFond = async () => {
    if (!newFondForm.nom.trim() || !newFondForm.file) return alert('Nom et image requis');
    setUploadingFond(true);
    try {
      const cId = newFondForm.concession_id === '' ? null : parseInt(newFondForm.concession_id);
      await stampytApi.uploadFond(newFondForm.file, newFondForm.nom, cId);
      setShowAddFondModal(false);
      setNewFondForm({ nom: '', file: null, concession_id: '' });
      loadFonds();
    } catch (err) {
      alert('Erreur lors de l\'upload du fond');
    } finally {
      setUploadingFond(false);
    }
  };

  const handleDeleteFond = async (fondId) => {
    if (!window.confirm('Supprimer ce fond ?')) return;
    try {
      await stampytApi.deleteFond(fondId);
      loadFonds();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  // ─── Supprimer un véhicule ────────────────────────────────────────────────────
  const handleDelete = async (vehiculeId) => {
    if (!window.confirm('Supprimer ce véhicule et toutes ses photos ?')) return;
    try {
      await stampytApi.deleteVehicule(vehiculeId);
      loadVehicules();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  // ─── Téléchargement zip des photos d'un véhicule ─────────────────────────────
  const [downloadingId, setDownloadingId] = useState(null);
  const handleDownloadZip = async (vehiculeId, immat) => {
    if (downloadingId) return;
    setDownloadingId(vehiculeId);
    try {
      const res = await stampytApi.downloadZip(vehiculeId);
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(immat || 'vehicule').replace(/[^a-zA-Z0-9-]/g, '_')}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      // Le backend renvoie un JSON même si responseType=blob. Il faut décoder le blob.
      let message = 'Erreur lors du téléchargement';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.error) message = parsed.error;
        } catch (_) {}
      }
      alert(message);
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Rendu — Liste VO en ligne ─────────────────────────────────────────────────
  const renderListe = () => (
    <div className="svo-liste">
      <div className="svo-liste-header">
        <h2>VO en ligne</h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
          Les photos sont prises depuis l'application terrain (parking.autodom97.fr)
        </p>
      </div>

      {listLoading ? (
        <div className="svo-loading">Chargement…</div>
      ) : listError ? (
        <div className="svo-error">{listError}</div>
      ) : vehicules.length === 0 ? (
        <div className="svo-empty">
          <p>Aucun véhicule. Les sessions photo sont créées depuis l'application terrain.</p>
        </div>
      ) : (
        <div className="svo-table-wrap">
          <table className="svo-table">
            <thead>
              <tr>
                <th>Immatriculation</th>
                <th>Marque / Modèle</th>
                <th>Date</th>
                <th>Photos</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vehicules.map((v) => (
                <tr key={v.id}>
                  <td><strong>{v.immatriculation}</strong></td>
                  <td>{[v.marque, v.modele].filter(Boolean).join(' ') || '—'}</td>
                  <td>{new Date(v.created_at).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <span className="svo-photo-count">
                      {v.nb_traitees}/{v.nb_photos}
                      {parseInt(v.nb_en_cours) > 0 && (
                        <span className="svo-spinner" title="Traitement en cours">⏳</span>
                      )}
                    </span>
                  </td>
                  <td>
                    <span
                      className="svo-statut-badge"
                      style={{ background: STATUT_COLORS[v.statut] + '22', color: STATUT_COLORS[v.statut] }}
                    >
                      {STATUT_LABELS[v.statut] || v.statut}
                    </span>
                  </td>
                  <td className="svo-actions-cell">
                    <button className="svo-link-btn" onClick={() => handleOpenDetail(v)}>Voir photos</button>
                    <button
                      className="svo-link-btn"
                      onClick={() => handleDownloadZip(v.id, v.immatriculation)}
                      disabled={downloadingId === v.id || parseInt(v.nb_photos) === 0}
                      title={parseInt(v.nb_photos) === 0 ? 'Aucune photo à télécharger' : `Télécharger ${v.nb_photos} photo(s)`}
                    >
                      {downloadingId === v.id ? '⏳ Téléchargement…' : '📥 Télécharger'}
                    </button>
                    <button className="svo-link-btn svo-link-danger" onClick={() => handleDelete(v.id)}>Supprimer</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ── Rendu — Détail photos d'un véhicule ──────────────────────────────────────
  const renderDetail = () => {
    if (!selectedVehicule) return null;

    const hasProcessing = detailPhotos.some(
      (p) => p.statut_traitement === 'en_cours' || p.statut_traitement === 'en_attente'
    );

    return (
      <div className="svo-detail">
        <div className="svo-detail-header">
          <button
            className="svo-back-btn"
            onClick={() => {
              setActiveTab('liste');
              if (pollingInterval) clearInterval(pollingInterval);
            }}
          >
            ← Retour
          </button>
          <div>
            <h2>{selectedVehicule.immatriculation}</h2>
            <span
              className="svo-statut-badge"
              style={{
                background: STATUT_COLORS[selectedVehicule.statut] + '22',
                color: STATUT_COLORS[selectedVehicule.statut],
              }}
            >
              {STATUT_LABELS[selectedVehicule.statut]}
            </span>
          </div>
          <button
            className="svo-btn svo-btn-secondary"
            onClick={() => handleDownloadZip(selectedVehicule.id, selectedVehicule.immatriculation)}
            disabled={downloadingId === selectedVehicule.id || detailPhotos.length === 0}
          >
            {downloadingId === selectedVehicule.id ? '⏳ Téléchargement…' : '📥 Télécharger toutes les photos'}
          </button>
        </div>

        {hasProcessing && (
          <div className="svo-processing-banner">
            ⚙️ Traitement IA en cours… Les photos se mettent à jour automatiquement.
          </div>
        )}

        {detailLoading ? (
          <div className="svo-loading">Chargement des photos…</div>
        ) : (
          <>
            {/* Zone d'upload multiple */}
            <div className="svo-upload-zone">
              <label className="svo-upload-drop">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      handleMultiUpload(selectedVehicule.id, Array.from(e.target.files));
                      e.target.value = '';
                    }
                  }}
                />
                <span className="svo-upload-icon">📷</span>
                <span>Cliquer pour ajouter des photos</span>
                <span className="svo-upload-hint">Les photos seront assignées aux vues manquantes dans l'ordre</span>
              </label>
            </div>

            <div className="svo-photo-grid">
              {/* Photos existantes */}
              {detailPhotos.map((p) => {
                const vue = VUES.find((v) => v.code === p.type_prise_vue);
                return (
                  <div key={p.id} className={`svo-photo-card ${p.statut_traitement}`}>
                    <div className="svo-photo-card-header">
                      <span>{vue?.label || p.type_prise_vue}</span>
                      <span className={`svo-photo-statut ${p.statut_traitement}`}>
                        {p.statut_traitement === 'termine' ? '✅' :
                         p.statut_traitement === 'erreur' ? '❌' :
                         p.statut_traitement === 'en_cours' ? '⏳' : '🔘'}
                      </span>
                    </div>

                    <div className={p.urlDetoure ? 'svo-photo-trio' : 'svo-photo-duo'}>
                      {/* Original */}
                      <div className="svo-photo-side">
                        <span className="svo-photo-side-label">Original</span>
                        {p.urlOriginal ? (
                          <a href={p.urlOriginal} target="_blank" rel="noopener noreferrer">
                            <img src={p.urlOriginal} alt="Original" className="svo-photo-img" />
                          </a>
                        ) : (
                          <div className="svo-photo-placeholder">—</div>
                        )}
                        {formatFileSize(p.tailleOriginal) && (
                          <span className="svo-photo-size">{formatFileSize(p.tailleOriginal)}</span>
                        )}
                      </div>

                      {/* Détouré (PNG transparent) */}
                      {p.urlDetoure && (
                        <div className="svo-photo-side">
                          <span className="svo-photo-side-label">Détouré</span>
                          <a href={p.urlDetoure} target="_blank" rel="noopener noreferrer">
                            <img src={p.urlDetoure} alt="Détouré" className="svo-photo-img svo-photo-detoure" />
                          </a>
                          {formatFileSize(p.tailleDetoure) && (
                            <span className="svo-photo-size">{formatFileSize(p.tailleDetoure)}</span>
                          )}
                        </div>
                      )}

                      {/* Final (avec fond) */}
                      <div className="svo-photo-side">
                        <span className="svo-photo-side-label">{p.urlDetoure ? 'Final' : 'Traité'}</span>
                        {p.urlTraite ? (
                          <>
                            <a href={p.urlTraite} target="_blank" rel="noopener noreferrer">
                              <img src={p.urlTraite} alt="Final" className="svo-photo-img" />
                            </a>
                            {formatFileSize(p.tailleTraite) && (
                              <span className="svo-photo-size">{formatFileSize(p.tailleTraite)}</span>
                            )}
                            <a
                              href={p.urlTraite}
                              download
                              className="svo-download-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              ↓ Télécharger
                            </a>
                          </>
                        ) : (
                          <div className="svo-photo-placeholder">
                            {p.statut_traitement === 'en_cours' ? '⏳' :
                             p.statut_traitement === 'erreur' ? '❌' : '—'}
                          </div>
                        )}
                      </div>
                    </div>

                    {p.statut_traitement === 'erreur' && p.erreur_message && (
                      <div className="svo-photo-error">{p.erreur_message}</div>
                    )}

                    {(p.statut_traitement === 'en_attente' || p.statut_traitement === 'erreur') && (
                      <div className="svo-photo-card-actions">
                        <button
                          className="svo-link-btn"
                          onClick={() => handleProcessOnePhoto(p.id)}
                        >
                          🖼️ Détourer cette photo
                        </button>
                      </div>
                    )}

                    {p.statut_traitement === 'termine' && p.s3_key_detoure && (
                      <div className="svo-photo-card-actions">
                        <button
                          className="svo-link-btn"
                          onClick={() => setShowFondModal(p.id)}
                        >
                          🎨 Changer le fond
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Vues manquantes — zones d'upload individuelles */}
              {VUES.filter(v => !detailPhotos.some(p => p.type_prise_vue === v.code)).map(vue => (
                <div key={vue.code} className="svo-photo-card svo-photo-card-empty">
                  <div className="svo-photo-card-header">
                    <span>{vue.label}</span>
                  </div>
                  <label className="svo-photo-upload-slot">
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleUploadPhoto(selectedVehicule.id, e.target.files[0], vue.code);
                          e.target.value = '';
                        }
                      }}
                    />
                    {uploading[vue.code] ? (
                      <span className="svo-upload-slot-text">⏳ Upload...</span>
                    ) : (
                      <>
                        <span className="svo-upload-slot-icon">+</span>
                        <span className="svo-upload-slot-text">Ajouter</span>
                      </>
                    )}
                  </label>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // ── Rendu — Onglet Fonds ───────────────────────────────────────────────────────
  const renderFonds = () => {
    const fondsGlobaux = fonds.filter(f => !f.concession_id);
    const fondsConcession = fonds.filter(f => f.concession_id);

    return (
      <div className="svo-liste">
        <div className="svo-liste-header">
          <h2>Bibliothèque de fonds</h2>
          <button className="svo-btn svo-btn-primary" onClick={() => setShowAddFondModal(true)}>
            + Nouveau fond
          </button>
        </div>

        {fondsLoading ? (
          <div className="svo-loading">Chargement…</div>
        ) : fonds.length === 0 ? (
          <div className="svo-empty">
            <p>Aucun fond. Ajoutez des images de fond pour personnaliser vos photos détourées.</p>
          </div>
        ) : (
          <>
            {fondsGlobaux.length > 0 && (
              <>
                <h3 style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '16px 0 8px' }}>Fonds communs</h3>
                <div className="svo-fonds-grid">
                  {fondsGlobaux.map(f => (
                    <div key={f.id} className="svo-fond-card">
                      <img src={f.url} alt={f.nom} className="svo-fond-img" />
                      <div className="svo-fond-info">
                        <span className="svo-fond-nom">{f.nom}</span>
                        <button className="svo-link-btn svo-link-danger" onClick={() => handleDeleteFond(f.id)}>
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {fondsConcession.length > 0 && (
              <>
                <h3 style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '16px 0 8px' }}>Fonds par concession</h3>
                <div className="svo-fonds-grid">
                  {fondsConcession.map(f => (
                    <div key={f.id} className="svo-fond-card">
                      <img src={f.url} alt={f.nom} className="svo-fond-img" />
                      <div className="svo-fond-info">
                        <span className="svo-fond-nom">{f.nom}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{f.concession_nom}</span>
                        <button className="svo-link-btn svo-link-danger" onClick={() => handleDeleteFond(f.id)}>
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  // ── Layout principal ─────────────────────────────────────────────────────────
  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={activeTab === 'liste' || activeTab === 'detail' ? 'active' : ''} onClick={() => setActiveTab('liste')}>
          📸 Photos VO
        </button>
        <button className={activeTab === 'fonds' ? 'active' : ''} onClick={() => setActiveTab('fonds')}>
          🎨 Fonds
        </button>
      </div>

      {activeTab === 'detail' && (
        <button className="svo-btn svo-btn-secondary" onClick={() => setActiveTab('liste')}>
          ← Retour à la liste
        </button>
      )}

      <div className="svo-content">
        {activeTab === 'liste' && renderListe()}
        {activeTab === 'detail' && renderDetail()}
        {activeTab === 'fonds' && renderFonds()}
      </div>

      {/* ── Modale création véhicule ── */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouveau véhicule</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="form-group">
                  <label>Immatriculation *</label>
                  <input
                    type="text"
                    value={createForm.immatriculation}
                    onChange={e => setCreateForm(f => ({ ...f, immatriculation: e.target.value.toUpperCase() }))}
                    placeholder="AB-123-CD"
                    style={{ textTransform: 'uppercase' }}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Marque</label>
                  <input
                    type="text"
                    value={createForm.marque}
                    onChange={e => setCreateForm(f => ({ ...f, marque: e.target.value }))}
                    placeholder="Peugeot, Renault..."
                  />
                </div>
                <div className="form-group">
                  <label>Modèle</label>
                  <input
                    type="text"
                    value={createForm.modele}
                    onChange={e => setCreateForm(f => ({ ...f, modele: e.target.value }))}
                    placeholder="208, Clio..."
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={handleCreateVehicule} disabled={creating}>
                {creating ? '⏳ Création...' : 'Créer le véhicule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale sélection de fond ── */}
      {showFondModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Choisir un fond</h2>
              <button className="modal-close" onClick={() => setShowFondModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {applyingFond && (
                <div className="svo-processing-banner">⏳ Application du fond en cours…</div>
              )}
              <div className="svo-fonds-picker">
                {/* Option fond blanc */}
                <button
                  type="button"
                  className="svo-fond-pick-card"
                  onClick={() => handleApplyFond(showFondModal, null)}
                >
                  <div className="svo-fond-pick-img" style={{ background: '#fff', border: '2px solid var(--border-default)' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Fond blanc</span>
                  </div>
                  <span className="svo-fond-pick-label">Blanc (défaut)</span>
                </button>

                {fonds.map(f => (
                  <button
                    type="button"
                    key={f.id}
                    className="svo-fond-pick-card"
                    onClick={() => handleApplyFond(showFondModal, f.id)}
                  >
                    <img src={f.url} alt={f.nom} className="svo-fond-pick-img" />
                    <span className="svo-fond-pick-label">{f.nom}</span>
                    {f.concession_nom && (
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{f.concession_nom}</span>
                    )}
                  </button>
                ))}
              </div>

              {fonds.length === 0 && !fondsLoading && (
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 16 }}>
                  Aucun fond personnalisé. Ajoutez-en dans l'onglet "Fonds".
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modale ajout de fond ── */}
      {showAddFondModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouveau fond</h2>
              <button className="modal-close" onClick={() => setShowAddFondModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="form-group">
                  <label>Nom du fond *</label>
                  <input
                    type="text"
                    value={newFondForm.nom}
                    onChange={e => setNewFondForm(f => ({ ...f, nom: e.target.value }))}
                    placeholder="Ex: Showroom, Promo été..."
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Image *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => setNewFondForm(f => ({ ...f, file: e.target.files[0] || null }))}
                  />
                  {newFondForm.file && (
                    <img
                      src={URL.createObjectURL(newFondForm.file)}
                      alt="Aperçu"
                      style={{ marginTop: 8, maxHeight: 150, borderRadius: 8, objectFit: 'cover', width: '100%' }}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Portée</label>
                  <select
                    value={newFondForm.concession_id}
                    onChange={e => setNewFondForm(f => ({ ...f, concession_id: e.target.value }))}
                  >
                    <option value="">Commun (toutes concessions)</option>
                    {user.role === 'super-admin' && user.concession_id && (
                      <option value={user.concession_id}>Ma concession uniquement</option>
                    )}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAddFondModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={handleAddFond} disabled={uploadingFond}>
                {uploadingFond ? '⏳ Upload...' : 'Ajouter le fond'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
